import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react"

// ** Third Party Components
import { Paperclip, Plus } from "lucide-react"

// ** Custom Element
import { Label } from "@components/common/label"
import { Button } from "@components/common/button"
import { Separator } from "@components/common/separator"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTrigger,
} from "@components/common/dialog"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@hooks/use-translator"

// ** helpers
import { cn } from "@pma/utils/helpers"

// ** Variables
import { BLOCKED_EXTENSIONS } from "@pma/utils/constants"

// ** Custom Components
import { RenderFileIcon } from "@components/files/files-view"
import { RenderUploadPreview } from "@components/files/upload-file"

interface Props {
  context: "task" | "note"
  setFile: Dispatch<SetStateAction<any>>
  uploadFunction?: CallableFunction
  reset: boolean
  setResetState?: Dispatch<SetStateAction<boolean>>
  resetState?: boolean
}
const AddFileToNote = ({
  context,
  setFile,
  uploadFunction,
  reset,
  resetState,
  setResetState,
}: Props) => {
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [isOpen, setIsOpen] = useState(false)
  const [filesArr, setFilesArr] = useState<any[]>([])
  const [nameUpdates, setNameUpdates] = useState<string[]>([])

  const handleFileChange = (event: any) => {
    if (event.target.files) {
      const fileArr = event.target.files
      let validFileType = true
      let validFileSize = true
      const maxSize = 1000 * 1024 * 1024 // 1000MB in bytes

      let totalSize = filesArr.reduce((acc: number, f: any) => acc + f.size, 0)
      for (let index = 0; index < fileArr.length; index++) {
        const nameArr = fileArr[index].name.split(".")
        const fileExt = nameArr[nameArr.length - 1]
        if (BLOCKED_EXTENSIONS.includes(fileExt)) {
          validFileType = false
          break
        }
        totalSize += fileArr[index].size
      }

      if (totalSize > maxSize) {
        validFileSize = false
      }

      if (!validFileType) {
        toast({
          title: translate("Error"),
          description: translate("Please select a valid file!"),
          variant: "error",
        })
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = ""
        }
      } else if (!validFileSize) {
        toast({
          title: translate("Error"),
          description: translate("File size must be under 1000MB!"),
          variant: "error",
        })
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = ""
        }
      } else {
        setFilesArr((prev) => [...prev, ...event.target.files])
        setNameUpdates((prev) => [...prev, ...[...event.target.files].map((file) => file?.name)])
      }
    }
  }

  const handleDrop = (event: any) => {
    event.preventDefault()
    if (event.dataTransfer.files) {
      const fileArr = event.dataTransfer.files
      let validFileType = true
      let validFileSize = true
      const maxSize = 1000 * 1024 * 1024 // 1000MB in bytes

      let totalSize = filesArr.reduce((acc: number, f: any) => acc + f.size, 0)
      for (let index = 0; index < fileArr.length; index++) {
        const nameArr = fileArr[index].name.split(".")
        const fileExt = nameArr[nameArr.length - 1]
        if (BLOCKED_EXTENSIONS.includes(fileExt)) {
          validFileType = false
          break
        }
        totalSize += fileArr[index].size
      }

      if (totalSize > maxSize) {
        validFileSize = false
      }

      if (!validFileType) {
        toast({
          title: translate("Error"),
          description: translate("Please select a valid file!"),
          variant: "error",
        })
      } else if (!validFileSize) {
        toast({
          title: translate("Error"),
          description: translate("File size must be under 1000MB!"),
          variant: "error",
        })
      } else {
        setFilesArr((prev) => [...prev, ...event.dataTransfer.files])
        setNameUpdates((prev) => [
          ...prev,
          ...[...event.dataTransfer.files].map((file) => file?.name),
        ])
      }
    }
  }

  const handleCancel = () => {
    setFilesArr([])
    resetInput()
  }

  const resetInput = () => {
    setFile([])
    setNameUpdates([])

    const input = document.getElementById("file-input") as HTMLInputElement
    if (input) {
      input.value = ""
    }
  }

  useEffect(() => {
    if (reset) handleCancel()
  }, [reset])

  useEffect(() => {
    if (resetState) {
      if (setResetState) setResetState(false)
      handleCancel()
    }
  }, [resetState])

  const handleAddMoreFiles = () => {
    if (fileInputRef.current) fileInputRef.current.click()
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open)
        if (!open) handleCancel()
      }}
    >
      <DialogTrigger>
        <Tooltip>
          <TooltipTrigger className='flex items-center gap-2 rounded-md bg-transparent p-1'>
            <Paperclip className='text-foreground' size={16} />{" "}
            {filesArr?.length > 0 && filesArr.length === 1 ? (
              [...filesArr]?.map((val: any) => (
                <small className='w-20 truncate text-ellipsis'>{val?.name}</small>
              ))
            ) : filesArr?.length > 0 && filesArr.length > 1 ? (
              <>
                <small className='absolute right-[80px] top-[1px] flex h-3 w-3 items-center justify-center rounded-full bg-slate-300 text-[10px] text-primary shadow-md'>
                  {filesArr?.length}
                </small>
                <RenderFileIcon type={"file"} size={"sm"} />
              </>
            ) : null}
          </TooltipTrigger>
          <TooltipContent className='bg-foreground'>
            {translate(`Attach file to ${context}`)}
          </TooltipContent>
        </Tooltip>
      </DialogTrigger>
      <DialogContent className='w-full max-w-xl'>
        <div className='flex w-full flex-col items-center justify-between'>
          <h4 className={cn("pb-2 font-semibold")}>
            {translate("File Upload")}{" "}
            <small>
              {filesArr?.length > 0 && `(${filesArr?.length} ${translate("files selected")})`}
            </small>
          </h4>
          <Separator className='w-full' />
        </div>
        <div className='flex items-center justify-between'>
          <input
            id='file-input'
            type='file'
            className='sr-only text-black'
            onChange={handleFileChange}
            multiple
            ref={fileInputRef}
          />
        </div>
        <div
          onDragOver={(event) => event.preventDefault()}
          onDrop={handleDrop}
          className={cn(
            "my-0 flex items-center justify-center rounded-md py-0 text-center",
            filesArr.length > 0 ? "overflow-y-auto" : "border border-dashed border-primary/50",
          )}
        >
          <div className='grid h-[200px] w-full grid-cols-2 gap-2 overflow-y-auto p-0.5'>
            {filesArr?.length === 0 && (
              <p className='col-span-2 flex w-full flex-col items-center justify-center text-muted-foreground'>
                {translate("Drag and drop a file")}
                <Button variant={"outline"} className='mt-2 flex justify-center'>
                  <Label htmlFor='file-input' className={cn("", isHebrew ? "rtl" : "rtl")}>
                    {translate("Choose File")}
                  </Label>
                </Button>
              </p>
            )}

            {filesArr &&
              filesArr?.map((file: any, index: number) => (
                <RenderUploadPreview
                  file={file}
                  index={index}
                  setNameUpdates={setNameUpdates}
                  setFile={setFilesArr}
                  nameUpdates={nameUpdates}
                  key={`${file.name}-${index}`}
                />
              ))}
            {filesArr?.length > 0 && (
              <div className='col-span-1 flex h-full w-full justify-center'>
                <Button
                  variant='outline'
                  className='flex h-28 w-full items-center gap-2 border-dashed border-primary/50'
                  onClick={handleAddMoreFiles}
                >
                  <Plus className='h-6 w-6 text-primary' />
                  <span>{translate("Add More Files")}</span>
                </Button>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className={cn('flex flex-row justify-evenly', isHebrew ? "!space-x-reverse" : "")}>
          <DialogClose className='flex'>
            <Button variant='outline' onClick={handleCancel}>
              {translate("Cancel")}{" "}
            </Button>
          </DialogClose>
          <Button
            variant='default'
            onClick={() => {
              const updatedFiles = filesArr.map((val: File, index: number) => {
                const updatedName = nameUpdates[index] || val.name
                const fileNameArr = updatedName.split(".")
                const initialFileName = val.name.split(".")
                const fileExt = initialFileName[initialFileName.length - 1]

                return new File([val], `${fileNameArr[0]}.${fileExt}`, { type: val.type })
              })
              setFile(updatedFiles)

              toast({
                title: translate("Success"),
                description: translate("Upload is in progress in the background"),
                variant: "success",
              })

              uploadFunction && uploadFunction()
              setIsOpen(false)
            }}
          >
            {translate("Done")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default AddFileToNote
